import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { IPAMStatCard } from './IPAMStatCard';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { 
  Network, Plus, Eye, Edit, Trash2, Copy, CheckCircle, AlertTriangle,
  ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ChevronsUpDown, Layers
} from 'lucide-react';
import { toast } from 'sonner';
import { usePermissions } from '../../hooks/usePermissions';

const initialFormData = {
  name: '',
  description: ''
};

const L2DomainsPage = () => {
  const { canCreate, canUpdate, canDelete } = usePermissions('ipam.l2_domains');
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ field: 'name', dir: 'asc' });
  
  // Column filters
  const [columnFilters, setColumnFilters] = useState({
    search: '',
    hasVlans: '' // 'with', 'without', or '' for all
  });
  
  // Stats
  const [stats, setStats] = useState({ total: 0, with_vlans: 0, empty: 0 });
  
  // CRUD State
  const [modalOpen, setModalOpen] = useState(false);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [copyingFrom, setCopyingFrom] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving, setSaving] = useState(false);

  // Sort handler
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/ipam/l2-domains/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchDomains = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ 
        page: pagination.page, 
        limit: pagination.limit,
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir
      });
      
      if (columnFilters.search) params.append('search', columnFilters.search);

      const response = await api.get(`/ipam/l2-domains?${params}`);
      setDomains(response.data.data || []);
      setPagination(prev => ({ ...prev, total: response.data.total, pages: response.data.pages }));
    } catch (error) {
      toast.error('Error al cargar L2 Domains');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchDomains(); }, [pagination.page, sortConfig]);
  
  useEffect(() => {
    const t = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchDomains();
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilters]);

  const handleFilterChange = (field, value) => {
    setColumnFilters(prev => ({ ...prev, [field]: value }));
  };

  // CRUD Handlers
  const handleCreate = () => {
    setEditingItem(null);
    setCopyingFrom(null);
    setFormData(initialFormData);
    setModalOpen(true);
  };

  const handleEdit = (domain) => {
    setEditingItem(domain);
    setCopyingFrom(null);
    setFormData({
      name: domain.name || '',
      description: domain.description || ''
    });
    setModalOpen(true);
  };

  const handleCopy = (domain) => {
    setCopyingFrom(domain.name);
    setEditingItem(null);
    setFormData({
      name: '', // Unique - empty
      description: domain.description || ''
    });
    setCopyModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    setSaving(true);
    try {
      if (editingItem) {
        await api.put(`/ipam/l2-domains/${editingItem.id}`, formData);
        toast.success('L2 Domain actualizado');
      } else {
        await api.post('/ipam/l2-domains', formData);
        toast.success('L2 Domain creado');
      }
      
      setModalOpen(false);
      setCopyModalOpen(false);
      setEditingItem(null);
      setCopyingFrom(null);
      setFormData(initialFormData);
      fetchDomains();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    
    try {
      await api.delete(`/ipam/l2-domains/${deleteConfirm.id}`);
      toast.success('L2 Domain eliminado');
      setDeleteConfirm(null);
      fetchDomains();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Filter domains based on hasVlans filter
  const filteredDomains = domains.filter(domain => {
    if (columnFilters.hasVlans === 'with') {
      return (domain.vlan_count || 0) > 0;
    } else if (columnFilters.hasVlans === 'without') {
      return (domain.vlan_count || 0) === 0;
    }
    return true;
  });

  return (
    <Layout>
      <div className="space-y-6" data-testid="l2-domains-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Network className="h-8 w-8 text-primary" strokeWidth={1.5} />
            <div>
              <h1 className="text-2xl font-bold text-foreground">L2 Domains</h1>
              <p className="text-sm text-muted-foreground">Dominios de Capa 2 (Broadcast)</p>
            </div>
          </div>
          {canCreate && (
            <Button onClick={handleCreate} data-testid="create-l2domain-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nuevo L2 Domain
            </Button>
          )}
        </div>

        {/* Stats Cards - Clickeable para filtrar */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <IPAMStatCard icon={Network} value={stats.total} label="Total Dominios"
            selected={columnFilters.hasVlans === ''}
            onClick={() => { setColumnFilters(prev => ({ ...prev, hasVlans: '' })); setPagination(p => ({ ...p, page: 1 })); }}
          />
          <IPAMStatCard icon={Layers} value={stats.with_vlans} label="Con VLANs" iconClassName="text-green-500"
            selected={columnFilters.hasVlans === 'with'}
            onClick={() => { setColumnFilters(prev => ({ ...prev, hasVlans: 'with' })); setPagination(p => ({ ...p, page: 1 })); }}
          />
          <IPAMStatCard icon={AlertTriangle} value={stats.empty} label="Sin VLANs" iconClassName="text-yellow-500"
            selected={columnFilters.hasVlans === 'without'}
            onClick={() => { setColumnFilters(prev => ({ ...prev, hasVlans: 'without' })); setPagination(p => ({ ...p, page: 1 })); }}
          />
        </div>

        {/* Table */}
        <div className="border rounded-lg overflow-hidden bg-card">
          <table className="w-full table-subtle">
            <thead>
              <tr className="border-b border-border/50">
                <th 
                  className="text-left p-3 font-medium text-xs uppercase tracking-wider cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center gap-1">
                    NOMBRE
                    <SortIcon field="name" />
                  </div>
                </th>
                <th 
                  className="text-left p-3 font-medium text-xs uppercase tracking-wider cursor-pointer hover:bg-muted/50"
                  onClick={() => handleSort('description')}
                >
                  <div className="flex items-center gap-1">
                    DESCRIPCIÓN
                    <SortIcon field="description" />
                  </div>
                </th>
                <th className="text-center p-3 font-medium text-xs uppercase tracking-wider">
                  VLANS
                </th>
                <th className="w-28 text-right p-3 font-medium text-xs uppercase tracking-wider">
                  ACCIONES
                </th>
              </tr>
              <tr className="border-b border-border/30 bg-muted/30">
                <th className="p-2">
                  <Input
                    placeholder="Buscar..."
                    value={columnFilters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="h-8 text-xs"
                  />
                </th>
                <th className="p-2"></th>
                <th className="p-2"></th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/30">
              {loading ? (
                <tr>
                  <td colSpan={4} className="text-center p-8 text-muted-foreground">
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                      Cargando...
                    </div>
                  </td>
                </tr>
              ) : filteredDomains.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center p-8">
                    <Network className="h-12 w-12 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
                    <p className="text-muted-foreground">
                      {columnFilters.hasVlans ? 'No hay dominios con este filtro' : 'No hay L2 Domains'}
                    </p>
                  </td>
                </tr>
              ) : (
                filteredDomains.map((domain) => (
                  <tr key={domain.id} className="hover:bg-muted/30" data-testid={`l2domain-row-${domain.id}`}>
                    <td className="p-3 font-medium">{domain.name}</td>
                    <td className="p-3 text-muted-foreground">{domain.description || '-'}</td>
                    <td className="p-3 text-center">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                        {domain.vlan_count || 0}
                      </span>
                    </td>
                    <td className="p-3 w-28">
                      <div className="flex justify-end gap-0.5">
                        <Link to={`/ipam/l2-domains/${domain.id}`}>
                          <button className="action-btn" title="Ver detalle">
                            <Eye className="h-3.5 w-3.5" />
                          </button>
                        </Link>
                        {canUpdate && (
                          <button onClick={() => handleEdit(domain)} className="action-btn action-btn-edit" title="Editar">
                            <Edit className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {canCreate && (
                          <button onClick={() => handleCopy(domain)} className="action-btn action-btn-copy" title="Copiar">
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {canDelete && (
                          <button onClick={() => setDeleteConfirm(domain)} className="action-btn-delete" title="Eliminar">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {pagination.pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border/50">
              <span className="text-sm text-muted-foreground">
                {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total}
              </span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))} disabled={pagination.page === 1}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm">Página {pagination.page} de {pagination.pages}</span>
                <Button variant="outline" size="sm" onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))} disabled={pagination.page === pagination.pages}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Create/Edit Modal */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Network className="h-5 w-5 text-primary" />
                {editingItem ? 'Editar L2 Domain' : 'Nuevo L2 Domain'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Nombre *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Ej: DC-Principal"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Descripción</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Descripción opcional"
                  className="mt-1.5"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : (editingItem ? 'Guardar' : 'Crear')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Copy Modal */}
        <Dialog open={copyModalOpen} onOpenChange={setCopyModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Copy className="h-5 w-5 text-primary" />
                Copiar L2 Domain
              </DialogTitle>
              {copyingFrom && <p className="text-sm text-muted-foreground">Copiando desde: {copyingFrom}</p>}
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <p className="text-sm text-yellow-600 dark:text-yellow-400 font-medium">Campos únicos (obligatorio)</p>
              </div>
              <div>
                <Label>Nombre *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Nuevo nombre"
                  className="mt-1.5"
                />
              </div>
              <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                <p className="text-sm text-green-600 dark:text-green-400 font-medium">Campos copiados</p>
              </div>
              <div>
                <Label>Descripción</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="mt-1.5"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCopyModalOpen(false)}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Creando...' : 'Crear Copia'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" />
                Confirmar Eliminación
              </DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground py-4">
              ¿Eliminar L2 Domain <strong>{deleteConfirm?.name}</strong>?
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
              <Button variant="destructive" onClick={handleDelete}>Eliminar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default L2DomainsPage;
